/**
 * Reminders to external delegates (molnfast-react#1613): the instance default
 * (`global/workOrderReminders`, Inställningar › Aviseringar) and a work
 * order's own schedule (`workOrders/active/{k}/reminders/schedule`). Both are
 * admin writes, validated by the rules with the same ranges as here. The sent
 * log is written by the functions job only.
 */
import { get, getDatabase, ref, set } from 'firebase/database';
import { recordActivity } from './activityApi';
import { requireAdmin } from './adminGuard';
import { formatDateForStorage } from '../Helpers/DateUtils';
import {
    DAYS_BEFORE_MAX,
    REPEAT_EVERY_DAYS_MAX,
    ReminderTiming,
    WORK_ORDER_REMINDERS_PATH,
    parseReminderDefaults,
} from '../types/workOrderReminders';

const assertValid = (timing: ReminderTiming): void => {
    const { daysBefore, repeatEveryDays } = timing;
    if (daysBefore !== undefined && !(Number.isInteger(daysBefore) && daysBefore >= 0 && daysBefore <= DAYS_BEFORE_MAX)) {
        throw new Error(`Dagar före förfallodatum ska vara 0–${DAYS_BEFORE_MAX}`);
    }
    if (repeatEveryDays !== undefined && !(Number.isInteger(repeatEveryDays) && repeatEveryDays >= 1 && repeatEveryDays <= REPEAT_EVERY_DAYS_MAX)) {
        throw new Error(`Upprepning ska vara 1–${REPEAT_EVERY_DAYS_MAX} dagar`);
    }
};

/** The node to store: no undefined fields, stamped with who and when. */
const record = (timing: ReminderTiming, uid: string) => ({
    ...(timing.daysBefore !== undefined ? { daysBefore: timing.daysBefore } : {}),
    ...(timing.repeatEveryDays !== undefined ? { repeatEveryDays: timing.repeatEveryDays } : {}),
    updatedBy: uid,
    updatedAt: formatDateForStorage(new Date()),
});

/** The instance default (absent → nothing before, overdue every 7 days). */
export async function getReminderDefaults(): Promise<ReminderTiming> {
    const snapshot = await get(ref(getDatabase(), WORK_ORDER_REMINDERS_PATH));
    return parseReminderDefaults(snapshot.val());
}

/**
 * Save the instance default. Both numbers off is still a stored node (an
 * absent one means the built-in default), so "inga påminnelser" survives.
 */
export async function saveReminderDefaults(timing: ReminderTiming): Promise<void> {
    const db = getDatabase();
    const uid = await requireAdmin(db, 'ändra påminnelserna');
    assertValid(timing);
    try {
        await set(ref(db, WORK_ORDER_REMINDERS_PATH), record(timing, uid));
        recordActivity('settings.updated');
    } catch (err) {
        console.error('Failed to save reminder defaults', err);
        throw new Error('Kunde inte spara påminnelserna. Försök igen.');
    }
}

/**
 * Set or turn off (null) an open work order's reminder schedule. Off is
 * stored as `{off: true}`, not removed, so a later delegation (iOS sends no
 * choice) cannot arm the instance default over it. The job reads it at its
 * next run; a changed schedule never resends a round already in the log for
 * the same due date.
 */
export async function saveWorkOrderReminderSchedule(
    workOrder: { key: string; buildingKey: string; title?: string },
    timing: ReminderTiming | null
): Promise<void> {
    const db = getDatabase();
    const uid = await requireAdmin(db, 'ändra påminnelserna');
    if (timing) assertValid(timing);
    // Only an open order is under active; never recreate one archived meanwhile.
    const status = await get(ref(db, `workOrders/active/${workOrder.key}/status`));
    if (!status.exists()) {
        throw new Error('Ärendet är inte längre öppet');
    }
    const scheduleRef = ref(db, `workOrders/active/${workOrder.key}/reminders/schedule`);
    try {
        await set(scheduleRef, timing
            ? record(timing, uid)
            : { off: true, updatedBy: uid, updatedAt: formatDateForStorage(new Date()) });
        recordActivity('workOrder.updated', { buildingKey: workOrder.buildingKey, targetKey: workOrder.key, label: workOrder.title });
    } catch (err) {
        console.error('Failed to save reminder schedule', err);
        throw new Error('Kunde inte spara påminnelserna. Försök igen.');
    }
}
